import type { Entry } from "../types";

export const rulesMessages = {
  // Queue filters
  filterAll: { ko: "전체", en: "All" },
  // Rule list search / sort
  ruleSearch: { ko: "규칙명, 설명 검색", en: "Search title or description" },
  ruleSort: { ko: "정렬", en: "Sort" },
  ruleSortNewest: { ko: "최신 등록순", en: "Newest first" },
  ruleSortOldest: { ko: "오래된 등록순", en: "Oldest first" },
  ruleSortTitle: { ko: "제목순", en: "Title" },
  ruleSortSeverity: { ko: "심각도 높은순", en: "Severity (high first)" },
  ruleSortCategory: { ko: "규칙 유형순", en: "Rule type" },
  ruleSearchEmpty: {
    ko: "검색 조건에 맞는 규칙이 없습니다.",
    en: "No rules match your search.",
  },
  // Rule states / list labels
  ruleActive: { ko: "활성", en: "Active" },
  ruleDraft: { ko: "초안", en: "Draft" },
  untitled: { ko: "(제목 없음)", en: "(untitled)" },
  ruleFormIntro: {
    ko: "규칙 유형을 먼저 선택한 뒤, 아래 예시를 참고해 검토 기준을 작성하세요.",
    en: "Choose a rule type first, then use the examples below to describe what reviewers should check.",
  },
  ruleTitlePlaceholder: {
    ko: "예: 수익률 보장 표현 금지",
    en: "For example, Prohibit guaranteed-return claims",
  },
  ruleDescriptionPlaceholder: {
    ko: "무엇을 확인하고, 언제 문제가 되는지 설명하세요.",
    en: "Explain what to check and when it should be flagged.",
  },
  visualDescriptionPlaceholder: {
    ko: "예: 상품 이미지 위에 과도한 수익을 암시하는 그래프가 있으면 검토가 필요합니다. 법정 고지 문구가 함께 있으면 허용합니다.",
    en: "For example, review images that imply excessive returns with charts; allow them when the required legal disclosure is present.",
  },
  ruleTypeProhibitedHelp: {
    ko: "입력한 표현이 자료에 포함되면 검토 대상으로 표시합니다.",
    en: "Flags material that contains any expression you enter.",
  },
  ruleTypeDisclaimerHelp: {
    ko: "입력한 고지 문구 중 하나 이상이 자료에 없으면 검토 대상으로 표시합니다.",
    en: "Flags material when none of the required disclaimer phrases is present.",
  },
  ruleTypeFieldHelp: {
    ko: "입력한 필수 항목이 자료에 없으면 검토 대상으로 표시합니다.",
    en: "Flags material when a required field is missing.",
  },
  ruleTypeComparisonHelp: {
    ko: "입력한 비교 표현이 발견되면 근거 자료가 필요한지 검토 대상으로 표시합니다.",
    en: "Flags comparison claims containing these trigger phrases for substantiation review.",
  },
  ruleTypeNumericHelp: {
    ko: "입력한 수치 표현이 발견되면 근거 자료가 필요한지 검토 대상으로 표시합니다.",
    en: "Flags numeric claims containing these trigger phrases for substantiation review.",
  },
  ruleTypeVisualHelp: {
    ko: "사람 검토자가 참고할 이미지 기준입니다. 자동 탐지나 자동 거부에는 사용되지 않습니다.",
    en: "Provides image criteria for human reviewers. It does not trigger automatic detection or rejection.",
  },
  ruleTypeGeneralHelp: {
    ko: "특정 자동 검사 없이 사람 검토자를 위한 일반 지침을 설명에 기록합니다.",
    en: "Records general guidance for reviewers without a specific automated phrase check.",
  },
  listProhibited: { ko: "금지 표현 (쉼표로 구분)", en: "Prohibited expressions (comma-separated)" },
  listProhibitedPlaceholder: {
    ko: "예: 원금 보장, 무조건 수익, 업계 최고",
    en: "For example, guaranteed principal, guaranteed return, best in the industry",
  },
  listRequiredDisclaimer: {
    ko: "필수 고지 문구 (쉼표로 구분)",
    en: "Required disclaimers (comma-separated)",
  },
  listRequiredDisclaimerPlaceholder: {
    ko: "예: 투자에는 손실 위험이 있습니다, 과거 실적은 미래 수익을 보장하지 않습니다",
    en: "For example, Investment involves risk of loss, Past performance does not guarantee future results",
  },
  listRequiredField: { ko: "필수 항목 (쉼표로 구분)", en: "Required fields (comma-separated)" },
  listRequiredFieldPlaceholder: {
    ko: "예: 상품명, 적용 기간, 문의처",
    en: "For example, product name, offer period, contact information",
  },
  listComparison: {
    ko: "비교 트리거 문구 (쉼표로 구분)",
    en: "Comparison trigger phrases (comma-separated)",
  },
  listComparisonPlaceholder: {
    ko: "예: 가장 낮은 수수료, 타사 대비, 업계 1위",
    en: "For example, lowest fee, compared with competitors, number one in the industry",
  },
  listNumeric: {
    ko: "수치 트리거 문구 (쉼표로 구분)",
    en: "Numeric trigger phrases (comma-separated)",
  },
  listNumericPlaceholder: {
    ko: "예: 연 5%, 수익률, 10년",
    en: "For example, 5% per year, return rate, 10 years",
  },
  listGeneral: { ko: "키워드 (선택)", en: "Keywords (optional)" },
  visualDetectionDescription: {
    ko: "시각적 탐지 기준 및 허용 예외",
    en: "Visual detection criteria and allowed exceptions",
  },
  visualReferenceHelp: {
    ko: "AI가 참고 이미지와 광고 자료를 비교해 의견을 제시하며, 사람 검토자에게도 이미지가 제공됩니다. 판단은 참고용이며 자동 거부에는 사용되지 않습니다. 이 규칙이 적용된 검토는 항상 담당자 확인을 거칩니다.",
    en: "The AI compares the material against these reference images and reports what it sees, and reviewers also receive the images. Its judgement is advisory and never causes automatic rejection: a review this rule applies to always goes to an officer.",
  },
  visualReferenceRequired: {
    ko: "활성화하려면 금지 사례(Positive) 이미지가 하나 이상 필요합니다.",
    en: "At least one positive prohibited example is required before activation.",
  },
  referenceImage: { ko: "참고 이미지", en: "Reference image" },
  referenceLabel: { ko: "예시 유형", en: "Example type" },
  referenceNote: { ko: "설명 (선택)", en: "Note (optional)" },
  referenceNotePlaceholder: {
    ko: "예: 허용되는 고지 문구 배치 예시",
    en: "For example, an acceptable legal-disclosure placement",
  },
  requiredCorrectionsPlaceholder: {
    ko: "예: ‘원금 보장’ 표현을 삭제하고 투자 위험 고지 문구를 추가하세요.",
    en: "For example, Remove “guaranteed principal” and add the investment-risk disclosure.",
  },
  upload: { ko: "업로드", en: "Upload" },
  // Member actions
} as const satisfies Record<string, Entry>;
